/*----------------------------------
- MARKETING PAGES: GLOSSARY TEXT
----------------------------------*/

/**
 * One catalog string with its trade words explained, drawn with the marketing `GlossaryTerm` (`dfn.wp-gl` and its
 * card, styled by the marketing sheet), the website's one glossary renderer since the old `.ud-term` one
 * (`GlossaryText`) left in the batch 6 sweep. It reads the enclosing `GlossarySection` scope ("first use per section"),
 * and with no section the string is its own section.
 *
 * @docs docs/features/website-marketing-layer
 * @adr  ADR-0110
 * @rule Never inside a heading, a link or a button: a `dfn` with a card inside a link would nest interactive content.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useRef } from 'preact/hooks';

import { splitGlossaryRun } from '@uniquedomains/components/glossary/split';
import { glossaryTermHref } from '@uniquedomains/components/glossary/terms';
import { GlossaryTerm } from '@uniquedomains/components/marketing/glossary-term/GlossaryTerm';

import { useGlossarySectionSeen } from '@/client/components/glossary/GlossarySection';

/*----------------------------------
- COMPONENT
----------------------------------*/

const MarketingGlossaryText = ({ children }: { children: string }) => {
    const sectionSeen = useGlossarySectionSeen();
    // The terms this run claimed on its first render, so rendering it again draws the same cards (see GlossarySection).
    const claimed = useRef<readonly string[]>([]);
    const { segments, claimed: nextClaimed } = splitGlossaryRun(children, sectionSeen ?? new Set<string>(), claimed.current);
    claimed.current = nextClaimed;

    return (
        <>
            {segments.map((segment, index) =>
                typeof segment === 'string' ? (
                    segment
                ) : (
                    <GlossaryTerm key={`${segment.term.id}-${index}`} title={segment.term.term} definition={segment.term.definition} href={glossaryTermHref(segment.term)}>
                        {segment.text}
                    </GlossaryTerm>
                ),
            )}
        </>
    );
};

export default MarketingGlossaryText;
